import React, { useState, useEffect, useCallback } from 'react';
import { authApi } from '../api';
import { useMasterData } from '../../utils/useMasterData';
import {
  Package, Search, Plus, Edit2, X, AlertTriangle, CheckCircle2,
  ChevronLeft, ChevronRight, Loader2, Filter, Ban, ArrowLeft,
  DollarSign, Layers, TrendingUp, BarChart3,
  ArrowDownLeft, ArrowUpRight, RotateCcw, Trash2, Activity
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

// ─── CONSTANTS ───────────────────────────────────────────────
const STONE_GRADES = ['VVS', 'VS', 'SI', 'I'];
const PER_PAGE = 20;

// ─── SHARED UI HELPERS ──────────────────────────────────────
const inputCls = "w-full p-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 text-sm placeholder-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors";
const selectCls = "w-full p-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 text-sm focus:border-blue-500 outline-none transition-colors";
const labelCls = "text-xs font-bold text-slate-500 uppercase tracking-wide";

const StatusBadge = ({ status, isActive }) => {
  if (isActive === false) {
    return <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase border bg-slate-100 text-slate-600 border-slate-300">INACTIVE</span>;
  }
  const styles = {
    OK: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    LOW: 'bg-amber-50 text-amber-700 border-amber-200',
    CRITICAL: 'bg-red-50 text-red-700 border-red-200',
    ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    DEPLETED: 'bg-slate-100 text-slate-500 border-slate-300',
    OPEN: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${styles[status] || 'bg-slate-100 text-slate-600 border-slate-300'}`}>
      {status || 'UNKNOWN'}
    </span>
  );
};

const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel, confirmLabel = 'Confirm', danger = false }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm border border-slate-200">
        <div className="p-5">
          <h3 className="font-bold text-base text-slate-800 flex items-center gap-2">
            <AlertTriangle size={18} className={danger ? 'text-red-600' : 'text-amber-600'} />
            {title}
          </h3>
          <p className="text-sm text-slate-500 mt-2">{message}</p>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-slate-200">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${danger ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);
  if (!message) return null;
  const colors = type === 'error' ? 'bg-red-600' : 'bg-emerald-600';
  return (
    <div className={`fixed top-6 right-6 z-[80] ${colors} text-white px-5 py-3 rounded-lg shadow-xl flex items-center gap-2`}>
      {type === 'error' ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 hover:bg-white/20 rounded p-0.5"><X size={14} /></button>
    </div>
  );
};

const SummaryCard = ({ title, value, icon: Icon, color }) => (
  <div className={`bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-3`}>
    <div className={`p-2.5 rounded-lg ${color}`}><Icon size={18} /></div>
    <div>
      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{title}</p>
      <p className="text-lg font-bold text-slate-800 mt-0.5">{value}</p>
    </div>
  </div>
);

// ─── MATERIAL FORM MODAL ─────────────────────────────────────
const MaterialFormModal = ({ isOpen, onClose, onSave, material = null }) => {
  const { getValues } = useMasterData(['MATERIAL_TYPE', 'UOM', 'METAL_PURITY', 'METAL_COLOR', 'STONE_TYPE', 'STONE_COLOR', 'STONE_CLARITY', 'STONE_CUT', 'STONE_SHAPE']);
  const materialTypes = getValues('MATERIAL_TYPE');
  const uomOptions = getValues('UOM');
  const purityOptions = getValues('METAL_PURITY');
  const metalColors = getValues('METAL_COLOR');
  const stoneTypes = getValues('STONE_TYPE');
  const stoneColors = getValues('STONE_COLOR');
  const stoneClarity = getValues('STONE_CLARITY');
  const stoneCut = getValues('STONE_CUT');
  const stoneShapes = getValues('STONE_SHAPE');
  const isEdit = !!material;
  const [type, setType] = useState('METAL');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    code: '', name: '', category: '', color: '', purity: '',
    unit: 'GRAM', price: '', reorder_level: '', reorder_quantity: '',
    stone_quality_grade: '', stone_clarity: '', stone_cut: '', stone_color: '',
    stone_type: '', custom_stone_type: '', carat_weight: '',
    shape: '', dimensions: '', hazard_type: '', description: ''
  });

  useEffect(() => {
    if (material) {
      setType(material.type || 'METAL');
      setFormData({
        code: material.code || '', name: material.name || '', category: material.category || '',
        color: material.color || '', purity: material.purity || '', unit: material.unit || 'GRAM',
        price: material.price || '', reorder_level: material.reorder_level || '',
        reorder_quantity: material.reorder_quantity || '',
        stone_quality_grade: material.stone_quality_grade || '', stone_clarity: material.stone_clarity || '',
        stone_cut: material.stone_cut || '', stone_color: material.stone_color || '',
        stone_type: material.stone_type || '', custom_stone_type: material.custom_stone_type || '',
        carat_weight: material.carat_weight || material.stone_weight || '',
        shape: material.details?.shape || '', dimensions: material.details?.dimensions || '',
        hazard_type: material.details?.hazard_type || '', description: material.details?.description || ''
      });
    } else {
      setType('METAL');
      setFormData({
        code: '', name: '', category: '', color: '', purity: '', unit: 'GRAM', price: '',
        reorder_level: '', reorder_quantity: '', stone_quality_grade: '', stone_clarity: '',
        stone_cut: '', stone_color: '', stone_type: '', custom_stone_type: '', carat_weight: '',
        shape: '', dimensions: '', hazard_type: '', description: ''
      });
    }
    setError('');
  }, [material, isOpen]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError('Material name is required'); return; }
    setSaving(true); setError('');
    try { await onSave({ ...formData, type }); onClose(); }
    catch (err) { setError(err?.response?.data?.error || err?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white rounded-t-xl z-10">
          <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
            <Package size={20} className="text-blue-600" /> {isEdit ? 'Edit' : 'Add New'} Material
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 transition-colors"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
          {/* Type Selector */}
          <div>
            <label className={labelCls}>Material Type</label>
            <div className="flex gap-2 mt-2">
              {materialTypes.map(t => (
                <button type="button" key={t} onClick={() => !isEdit && setType(t)} disabled={isEdit}
                  className={`px-4 py-2 text-xs font-bold rounded-lg border transition-all ${type === t
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/20'
                    : 'bg-slate-50 text-slate-500 border-slate-300 hover:bg-slate-100'} ${isEdit ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          {/* Basic Info */}
          <div className="space-y-1">
            <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-3">Basic Information</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1"><label className={labelCls}>Material Code</label>
                <input name="code" value={formData.code} onChange={handleChange} className={inputCls} placeholder="e.g. GOLD-24K (auto if blank)" disabled={isEdit} /></div>
              <div className="space-y-1"><label className={labelCls}>Material Name *</label>
                <input required name="name" value={formData.name} onChange={handleChange} className={inputCls}
                  placeholder={type === 'STONE' ? 'e.g. Round Diamond 0.5ct' : 'e.g. 22K Gold Wire'} /></div>
              <div className="space-y-1"><label className={labelCls}>Category</label>
                <input name="category" value={formData.category} onChange={handleChange} className={inputCls}
                  placeholder={type === 'METAL' ? 'Gold, Silver, Brass' : type === 'STONE' ? 'Diamond, Ruby' : type === 'CONSUMABLE' ? 'Acid, Polish' : 'Clasp, Ring base'} /></div>
              <div className="space-y-1"><label className={labelCls}>UOM</label>
                <select name="unit" value={formData.unit} onChange={handleChange} className={selectCls}>
                  {uomOptions.map(u => <option key={u} value={u}>{u}</option>)}
                </select></div>
              <div className="space-y-1"><label className={labelCls}>Cost Price</label>
                <input type="number" step="0.01" name="price" value={formData.price} onChange={handleChange} className={inputCls} placeholder="0.00" /></div>
            </div>
          </div>
          {/* Metal */}
          {type === 'METAL' && (
            <div className="space-y-1">
              <div className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-3">Metal Properties</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1"><label className={labelCls}>Purity</label>
                  <select name="purity" value={formData.purity} onChange={handleChange} className={selectCls}>
                    <option value="">Select Purity</option>{purityOptions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select></div>
                <div className="space-y-1"><label className={labelCls}>Color</label>
                  <select name="color" value={formData.color} onChange={handleChange} className={selectCls}>
                    <option value="">Select Color</option>{metalColors.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
              </div>
            </div>
          )}
          {/* Stone */}
          {type === 'STONE' && (
            <div className="space-y-1">
              <div className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-3">Stone Properties (GIA Scale)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1"><label className={labelCls}>Quality Grade</label>
                  <select name="stone_quality_grade" value={formData.stone_quality_grade} onChange={handleChange} className={selectCls}>
                    <option value="">Select</option>{STONE_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select></div>
                <div className="space-y-1"><label className={labelCls}>Clarity</label>
                  <select name="stone_clarity" value={formData.stone_clarity} onChange={handleChange} className={selectCls}>
                    <option value="">Select</option>{stoneClarity.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div className="space-y-1"><label className={labelCls}>Cut</label>
                  <select name="stone_cut" value={formData.stone_cut} onChange={handleChange} className={selectCls}>
                    <option value="">Select</option>{stoneCut.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div className="space-y-1"><label className={labelCls}>Color (GIA)</label>
                  <select name="stone_color" value={formData.stone_color} onChange={handleChange} className={selectCls}>
                    <option value="">Select</option>{stoneColors.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div className="space-y-1"><label className={labelCls}>Stone Type</label>
                  <select name="stone_type" value={formData.stone_type} onChange={handleChange} className={selectCls}>
                    <option value="">Select</option>{stoneTypes.map(option => <option key={option} value={option}>{option.replaceAll('_', ' ')}</option>)}
                  </select></div>
                {formData.stone_type === 'CUSTOM' && (
                  <div className="space-y-1"><label className={labelCls}>Custom Stone Type</label>
                    <input name="custom_stone_type" value={formData.custom_stone_type} onChange={handleChange} className={inputCls} placeholder="Enter custom stone type" /></div>
                )}
                <div className="space-y-1"><label className={labelCls}>Carat Weight (ct)</label>
                  <input type="number" step="0.001" name="carat_weight" value={formData.carat_weight} onChange={handleChange} className={inputCls} placeholder="0.000" /></div>
                <div className="space-y-1"><label className={labelCls}>Shape</label>
                  <select name="shape" value={formData.shape} onChange={handleChange} className={selectCls}>
                    <option value="">Select</option>{stoneShapes.map(shape => <option key={shape} value={shape}>{shape}</option>)}
                  </select></div>
              </div>
            </div>
          )}
          {/* Consumable */}
          {type === 'CONSUMABLE' && (
            <div className="space-y-1">
              <div className="text-xs font-bold text-orange-600 uppercase tracking-wider mb-3">Consumable Details</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1"><label className={labelCls}>Hazard Type</label>
                  <input name="hazard_type" value={formData.hazard_type} onChange={handleChange} className={inputCls} placeholder="Corrosive, Flammable" /></div>
                <div className="space-y-1"><label className={labelCls}>Description</label>
                  <input name="description" value={formData.description} onChange={handleChange} className={inputCls} placeholder="Brief description" /></div>
              </div>
            </div>
          )}
          {/* Inventory Settings */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="text-xs font-bold text-green-600 uppercase tracking-wider mb-3">Inventory Settings</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1"><label className={labelCls}>Reorder Level</label>
                <input type="number" step="0.001" name="reorder_level" value={formData.reorder_level} onChange={handleChange} className={inputCls} placeholder="Min stock trigger" /></div>
              <div className="space-y-1"><label className={labelCls}>Reorder Quantity</label>
                <input type="number" step="0.001" name="reorder_quantity" value={formData.reorder_quantity} onChange={handleChange} className={inputCls} placeholder="Suggested order qty" /></div>
            </div>
          </div>
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-slate-500 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? 'Update Material' : 'Create Material'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── ADD BATCH MODAL ─────────────────────────────────────────
const AddBatchModal = ({ isOpen, onClose, onSave, material }) => {
  const { getValues } = useMasterData('METAL_PURITY');
  const purityOptions = getValues('METAL_PURITY');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [formData, setFormData] = useState({
    supplier_id: '',
    quantity_received: '', unit_price: '', supplier_name: '',
    invoice_number: '', receipt_date: '', purity: '', notes: '', expiry_date: ''
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        supplier_id: '',
        quantity_received: '', unit_price: '', supplier_name: '',
        invoice_number: '', receipt_date: new Date().toISOString().split('T')[0],
        purity: material?.purity || '', notes: '', expiry_date: ''
      });
      setError('');
    }
  }, [isOpen, material]);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    setSuppliersLoading(true);
    authApi.get('/sales/suppliers', { params: { page: 1, limit: 200 } })
      .then((res) => {
        if (!mounted) return;
        setSuppliers(res?.data?.data || []);
      })
      .catch(() => {
        if (!mounted) return;
        setSuppliers([]);
      })
      .finally(() => {
        if (mounted) setSuppliersLoading(false);
      });

    return () => { mounted = false; };
  }, [isOpen]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(formData.supplier_id));

  const handleSupplierChange = (e) => {
    const supplierId = e.target.value;
    const supplier = suppliers.find((row) => String(row.id) === supplierId);
    setFormData((prev) => ({
      ...prev,
      supplier_id: supplierId,
      supplier_name: supplier?.name || '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.quantity_received || parseFloat(formData.quantity_received) <= 0) {
      setError('Quantity must be greater than 0'); return;
    }
    setSaving(true); setError('');
    try { await onSave(formData); onClose(); }
    catch (err) { setError(err?.response?.data?.error || err?.message || 'Failed to create batch'); }
    finally { setSaving(false); }
  };

  if (!isOpen || !material) return null;

  const isMetal = material.type === 'METAL';
  const isConsumable = material.type === 'CONSUMABLE';

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-slate-200">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white rounded-t-xl z-10">
          <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
            <Plus size={20} className="text-green-600" /> Add Batch — {material.name}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className={labelCls}>Quantity Received ({material.unit}) *</label>
              <input required type="number" step="0.001" name="quantity_received" value={formData.quantity_received}
                onChange={handleChange} className={inputCls} placeholder="0.000" />
            </div>
            <div className="space-y-1">
              <label className={labelCls}>Unit Price (Rs/{material.unit}) *</label>
              <input required type="number" step="0.01" name="unit_price" value={formData.unit_price}
                onChange={handleChange} className={inputCls} placeholder="0.00" />
            </div>
          </div>

          {/* Computed total */}
          {formData.quantity_received && formData.unit_price && (
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex justify-between items-center">
              <span className="text-xs font-bold text-slate-500">Total Value</span>
              <span className="text-lg font-bold text-emerald-600">
                Rs {(parseFloat(formData.quantity_received) * parseFloat(formData.unit_price)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}

          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4">
            <div className="text-xs font-bold text-slate-500 uppercase">Supplier Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1"><label className={labelCls}>Select Supplier</label>
                <select
                  name="supplier_id"
                  value={formData.supplier_id}
                  onChange={handleSupplierChange}
                  className={selectCls}
                >
                  <option value="">{suppliersLoading ? 'Loading suppliers...' : 'Select supplier from master'}</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}{supplier.city ? ` - ${supplier.city}` : ''}{supplier.phone ? ` (${supplier.phone})` : ''}
                    </option>
                  ))}
                </select></div>
              <div className="space-y-1"><label className={labelCls}>Invoice Number</label>
                <input name="invoice_number" value={formData.invoice_number} onChange={handleChange} className={inputCls} placeholder="INV-XXXX" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1"><label className={labelCls}>Supplier Name</label>
                <input name="supplier_name" value={formData.supplier_name} readOnly className={`${inputCls} bg-slate-100 text-slate-600`} placeholder="Selected supplier name" /></div>
              <div className="space-y-1"><label className={labelCls}>Contact Details</label>
                <input
                  value={selectedSupplier ? [selectedSupplier.contact_person, selectedSupplier.phone].filter(Boolean).join(' | ') : ''}
                  readOnly
                  className={`${inputCls} bg-slate-100 text-slate-600`}
                  placeholder="Supplier contact will appear here"
                /></div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1"><label className={labelCls}>Address</label>
                <textarea
                  value={selectedSupplier?.full_address || ''}
                  readOnly
                  rows={2}
                  className={`${inputCls} resize-none bg-slate-100 text-slate-600`}
                  placeholder="Supplier address will appear here"
                /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1"><label className={labelCls}>GSTIN</label>
                <input value={selectedSupplier?.gstin || ''} readOnly className={`${inputCls} bg-slate-100 text-slate-600`} placeholder="GSTIN" /></div>
              <div className="space-y-1"><label className={labelCls}>Payment Terms</label>
                <input
                  value={selectedSupplier ? `${selectedSupplier.payment_terms || 'IMMEDIATE'}${selectedSupplier.credit_days ? ` | ${selectedSupplier.credit_days} days` : ''}` : ''}
                  readOnly
                  className={`${inputCls} bg-slate-100 text-slate-600`}
                  placeholder="Payment terms"
                /></div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className={labelCls}>Receipt Date</label>
              <input type="date" name="receipt_date" value={formData.receipt_date} onChange={handleChange} className={inputCls} />
            </div>
            {isMetal && (
              <div className="space-y-1"><label className={labelCls}>Purity</label>
                <select name="purity" value={formData.purity} onChange={handleChange} className={selectCls}>
                  <option value="">Select</option>{purityOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select></div>
            )}
            {isConsumable && (
              <div className="space-y-1"><label className={labelCls}>Expiry Date</label>
                <input type="date" name="expiry_date" value={formData.expiry_date} onChange={handleChange} className={inputCls} /></div>
            )}
          </div>

          <div className="space-y-1">
            <label className={labelCls}>Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2}
              className={`${inputCls} resize-none`} placeholder="Optional notes..." />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-bold rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />}
              Confirm Batch Receipt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── MONTHLY SUMMARY CHART ───────────────────────────────────
const MonthlyChart = ({ materialId, year, onBarClick }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    if (!materialId) return;
    setLoading(true);
    authApi.get('/inventory/batches/monthly-summary', { params: { material_id: materialId, year } })
      .then(res => setData(res.data.summary || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [materialId, year]);

  if (loading) {
    return <div className="flex items-center justify-center h-48 text-slate-500"><Loader2 size={20} className="animate-spin mr-2" /> Loading chart...</div>;
  }

  const hasData = data.some(d => d.total_qty > 0);
  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-slate-500">
        <BarChart3 size={32} className="mb-2 opacity-30" />
        <p className="text-sm">No batch data for {year}</p>
      </div>
    );
  }

  const handleClick = (entry) => {
    if (entry && entry.activePayload && entry.activePayload.length > 0) {
      const month = entry.activePayload[0].payload.month;
      setSelectedMonth(month === selectedMonth ? null : month);
      if (onBarClick) onBarClick(month === selectedMonth ? null : month);
    }
  };

  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} onClick={handleClick} style={{ cursor: 'pointer' }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="month_name" tick={{ fill: '#9CA3AF', fontSize: 11 }} axisLine={{ stroke: '#374151' }} />
          <YAxis tick={{ fill: '#9CA3AF', fontSize: 11 }} axisLine={{ stroke: '#374151' }} />
          <Tooltip
            contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A' }}
            labelStyle={{ color: '#475569', fontWeight: 'bold' }}
            formatter={(value, name) => {
              if (name === 'total_qty') return [`${value.toFixed(2)}`, 'Qty Received'];
              if (name === 'total_cost') return [`Rs ${value.toFixed(2)}`, 'Total Cost'];
              return [value, name];
            }}
          />
          <Bar dataKey="total_qty" radius={[4, 4, 0, 0]} maxBarSize={40}>
            {data.map((entry, idx) => (
              <Cell key={idx} fill={entry.month === selectedMonth ? '#3B82F6' : (entry.total_qty > 0 ? '#10B981' : '#CBD5E1')}
                stroke={entry.month === selectedMonth ? '#60A5FA' : 'none'} strokeWidth={2} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── TRANSACTION TYPE BADGE ──────────────────────────────────
const TxnTypeBadge = ({ type }) => {
  const config = {
    RECEIPT: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: ArrowDownLeft },
    INWARD: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: ArrowDownLeft },
    ISSUE: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: ArrowUpRight },
    RETURN: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', icon: RotateCcw },
    WASTAGE: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: AlertTriangle },
    SCRAP: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: Trash2 },
    ADJUSTMENT: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: Activity },
  };
  const c = config[type] || { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300', icon: Activity };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${c.bg} ${c.text} ${c.border}`}>
      <Icon size={10} /> {type}
    </span>
  );
};

// ─── MATERIAL DETAIL VIEW ────────────────────────────────────
const MaterialDetail = ({ material, onBack, onToast, canEdit = false }) => {
  const [activeTab, setActiveTab] = useState('batches');
  const [batches, setBatches] = useState([]);
  const [batchLoading, setBatchLoading] = useState(true);
  const [batchPage, setBatchPage] = useState(1);
  const [batchTotalPages, setBatchTotalPages] = useState(1);
  const [batchTotal, setBatchTotal] = useState(0);
  const [showAddBatch, setShowAddBatch] = useState(false);
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(null);

  // Transactions (ledger) state
  const [ledger, setLedger] = useState(null);
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnPage, setTxnPage] = useState(1);
  const [txnTypeFilter, setTxnTypeFilter] = useState('');
  const [txnFromDate, setTxnFromDate] = useState('');
  const [txnToDate, setTxnToDate] = useState('');
  const [txnJobCard, setTxnJobCard] = useState('');

  const fetchLedger = useCallback(async () => {
    setTxnLoading(true);
    try {
      const params = { page: txnPage, per_page: 30 };
      if (txnFromDate) params.from_date = txnFromDate;
      if (txnToDate) params.to_date = txnToDate;
      const res = await authApi.get(`/inventory/materials/${material.id}/ledger`, { params });
      let txns = res.data.transactions || [];
      // Client-side filters for type and job card
      if (txnTypeFilter) txns = txns.filter(t => t.transaction_type === txnTypeFilter);
      if (txnJobCard) txns = txns.filter(t =>
        (t.job_card_code || '').toLowerCase().includes(txnJobCard.toLowerCase()) ||
        String(t.job_card_id || '').includes(txnJobCard)
      );
      setLedger({ ...res.data, transactions: txns });
    } catch { setLedger(null); }
    finally { setTxnLoading(false); }
  }, [material.id, txnPage, txnFromDate, txnToDate, txnTypeFilter, txnJobCard]);

  useEffect(() => {
    if (activeTab === 'transactions') fetchLedger();
  }, [activeTab, fetchLedger]);

  const fetchBatches = useCallback(async () => {
    setBatchLoading(true);
    try {
      const res = await authApi.get(`/inventory/materials/${material.id}/batches`, {
        params: { page: batchPage, per_page: 15 }
      });
      setBatches(res.data.batches || []);
      setBatchTotalPages(res.data.pages || 1);
      setBatchTotal(res.data.total || 0);
    } catch { setBatches([]); }
    finally { setBatchLoading(false); }
  }, [material.id, batchPage]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const handleAddBatch = async (formData) => {
    await authApi.post(`/inventory/materials/${material.id}/batches`, {
      quantity_received: parseFloat(formData.quantity_received),
      unit_price: parseFloat(formData.unit_price) || 0,
      supplier_name: formData.supplier_name,
      invoice_number: formData.invoice_number,
      receipt_date: formData.receipt_date,
      purity: formData.purity,
      notes: formData.notes,
      expiry_date: formData.expiry_date || null
    });
    onToast('Batch created successfully', 'success');
    fetchBatches();
    setChartYear(y => y); // force chart refresh via key
  };

  // Summary stats from batch list
  const totalStock = batches.reduce((s, b) => s + b.available_quantity, 0);
  const totalValue = batches.reduce((s, b) => s + b.total_value, 0);

  // Filter batches by month from chart click
  const displayBatches = filterMonth
    ? batches.filter(b => {
        const d = new Date(b.receipt_date);
        return d.getMonth() + 1 === filterMonth;
      })
    : batches;

  const tabs = [
    { key: 'batches', label: 'Batch History', icon: Layers },
    { key: 'chart', label: 'Monthly Summary', icon: BarChart3 },
    { key: 'transactions', label: 'Transactions', icon: Activity },
  ];

  return (
    <div className="h-full flex flex-col bg-slate-50 text-slate-800">
      <AddBatchModal
        isOpen={showAddBatch}
        onClose={() => setShowAddBatch(false)}
        onSave={handleAddBatch}
        material={material}
      />

      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-slate-200">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={onBack} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-slate-700">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-800 truncate">{material.name}</h1>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-xs font-mono text-slate-500">{material.code}</span>
              <span className={`text-xs font-bold ${
                material.type === 'METAL' ? 'text-amber-600' : material.type === 'STONE' ? 'text-purple-600' : material.type === 'CONSUMABLE' ? 'text-orange-600' : 'text-cyan-600'
              }`}>{material.type}</span>
              {material.purity && <span className="text-xs text-slate-500">{material.purity}</span>}
            </div>
          </div>
          {canEdit && (
            <button onClick={() => setShowAddBatch(true)}
              className="min-h-[44px] flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg shadow-lg shadow-green-600/20 transition-colors">
              <Plus size={16} /> Add Batch
            </button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard title="Total Stock" value={`${totalStock.toFixed(2)} ${material.unit}`} icon={Package} color="bg-blue-50 text-blue-600" />
          <SummaryCard title="Total Value" value={`Rs ${totalValue.toLocaleString('en-IN', { minimumFractionDigits: 0 })}`} icon={DollarSign} color="bg-emerald-50 text-emerald-600" />
          <SummaryCard title="Batches" value={batchTotal} icon={Layers} color="bg-purple-50 text-purple-600" />
          <SummaryCard title="Reorder Level" value={`${material.reorder_level?.toFixed(2) || '0'} ${material.unit}`} icon={TrendingUp} color="bg-amber-50 text-amber-600" />
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 sm:px-6 border-b border-slate-200 flex gap-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setActiveTab(t.key); setFilterMonth(null); }}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-600'}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        {/* ── BATCH HISTORY TAB ── */}
        {activeTab === 'batches' && (
          <div>
            {filterMonth && (
              <div className="px-4 sm:px-6 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                <span className="text-xs text-blue-700">Filtering: Month {filterMonth}</span>
                <button onClick={() => setFilterMonth(null)} className="text-xs text-blue-700 hover:text-blue-800 flex items-center gap-1">
                  <X size={12} /> Clear filter
                </button>
              </div>
            )}
            {batchLoading ? (
              <div className="flex items-center justify-center h-48 text-slate-500">
                <Loader2 size={24} className="animate-spin mr-2" /> Loading batches...
              </div>
            ) : displayBatches.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                <Layers size={36} className="mb-2 opacity-30" />
                <p className="text-sm">No batches found</p>
                {canEdit && <button onClick={() => setShowAddBatch(true)} className="mt-2 text-green-600 text-sm hover:underline">+ Add first batch</button>}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-white border-b border-slate-200 sticky top-0 z-[5]">
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Batch Code</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden sm:table-cell">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Supplier</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Received</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden sm:table-cell">Issued</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Available</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Price/{material.unit}</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden lg:table-cell">Value</th>
                      <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayBatches.map(b => (
                      <tr key={b.id} className="border-b border-slate-200 hover:bg-white/50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-blue-600">{b.batch_code}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs hidden sm:table-cell">{b.receipt_date || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 hidden md:table-cell truncate max-w-[140px]">{b.supplier_name || '—'}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-800">{b.quantity_received.toFixed(3)}</td>
                        <td className="px-4 py-3 text-right font-mono text-orange-600 hidden sm:table-cell">{b.quantity_issued.toFixed(3)}</td>
                        <td className={`px-4 py-3 text-right font-mono font-bold ${b.available_quantity <= 0 ? 'text-slate-500' : 'text-emerald-600'}`}>
                          {b.available_quantity.toFixed(3)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-500 hidden md:table-cell">{b.unit_price.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-500 hidden lg:table-cell">
                          Rs {b.total_value.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                        </td>
                        <td className="px-4 py-3 text-center"><StatusBadge status={b.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Batch Pagination */}
            {batchTotalPages > 1 && !filterMonth && (
              <div className="px-4 sm:px-6 py-3 border-t border-slate-200 flex items-center justify-between">
                <p className="text-xs text-slate-500">Page {batchPage} of {batchTotalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setBatchPage(p => Math.max(1, p - 1))} disabled={batchPage <= 1}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-30 flex items-center gap-1">
                    <ChevronLeft size={14} /> Prev
                  </button>
                  <button onClick={() => setBatchPage(p => Math.min(batchTotalPages, p + 1))} disabled={batchPage >= batchTotalPages}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-30 flex items-center gap-1">
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── MONTHLY SUMMARY TAB ── */}
        {activeTab === 'chart' && (
          <div className="p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <BarChart3 size={16} className="text-blue-600" /> Monthly Batch Intake
              </h3>
              <div className="flex items-center gap-2">
                <button onClick={() => setChartYear(y => y - 1)} className="px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded"><ChevronLeft size={14} /></button>
                <span className="text-sm font-bold text-slate-800 min-w-[48px] text-center">{chartYear}</span>
                <button onClick={() => setChartYear(y => y + 1)} disabled={chartYear >= new Date().getFullYear()}
                  className="px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronRight size={14} /></button>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <MonthlyChart
                key={`${material.id}-${chartYear}`}
                materialId={material.id}
                year={chartYear}
                onBarClick={(month) => { setFilterMonth(month); if (month) setActiveTab('batches'); }}
              />
            </div>
            <p className="text-xs text-slate-500 text-center">Click a bar to filter batch history by that month</p>
          </div>
        )}

        {/* ── TRANSACTIONS TAB ── */}
        {activeTab === 'transactions' && (
          <div>
            {/* Filter Bar */}
            <div className="px-4 sm:px-6 py-3 border-b border-slate-200 flex flex-col sm:flex-row gap-3">
              <select value={txnTypeFilter} onChange={(e) => { setTxnTypeFilter(e.target.value); setTxnPage(1); }}
                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:border-blue-500 outline-none">
                <option value="">All Types</option>
                {['RECEIPT', 'ISSUE', 'RETURN', 'WASTAGE', 'SCRAP', 'ADJUSTMENT'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500">From</label>
                <input type="date" value={txnFromDate} onChange={(e) => { setTxnFromDate(e.target.value); setTxnPage(1); }}
                  className="px-2 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:border-blue-500 outline-none" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500">To</label>
                <input type="date" value={txnToDate} onChange={(e) => { setTxnToDate(e.target.value); setTxnPage(1); }}
                  className="px-2 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:border-blue-500 outline-none" />
              </div>
              <div className="flex items-center gap-2 flex-1">
                <input value={txnJobCard} onChange={(e) => setTxnJobCard(e.target.value)}
                  placeholder="Job Card ID / Code..."
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:border-blue-500 outline-none" />
              </div>
              {(txnTypeFilter || txnFromDate || txnToDate || txnJobCard) && (
                <button onClick={() => { setTxnTypeFilter(''); setTxnFromDate(''); setTxnToDate(''); setTxnJobCard(''); setTxnPage(1); }}
                  className="px-3 py-2 text-xs text-slate-500 hover:bg-slate-100 rounded-lg border border-slate-200 flex items-center gap-1">
                  <X size={12} /> Clear
                </button>
              )}
            </div>

            {/* Ledger Summary */}
            {ledger && !txnLoading && (
              <div className="px-4 sm:px-6 py-3 border-b border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Opening Balance</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{parseFloat(ledger.opening_balance || 0).toFixed(3)}</p>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Closing Balance</p>
                  <p className="text-sm font-bold text-emerald-600 mt-0.5">{parseFloat(ledger.closing_balance || 0).toFixed(3)}</p>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Total Received</p>
                  <p className="text-sm font-bold text-green-600 mt-0.5">+{parseFloat(ledger.total_received || 0).toFixed(3)}</p>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <p className="text-[10px] font-bold text-slate-500 uppercase">Total Issued</p>
                  <p className="text-sm font-bold text-blue-600 mt-0.5">-{parseFloat(ledger.total_issued || 0).toFixed(3)}</p>
                </div>
              </div>
            )}

            {/* Transactions Table */}
            {txnLoading ? (
              <div className="flex items-center justify-center h-48 text-slate-500">
                <Loader2 size={24} className="animate-spin mr-2" /> Loading transactions...
              </div>
            ) : !ledger || ledger.transactions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                <Activity size={36} className="mb-2 opacity-30" />
                <p className="text-sm">No transactions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-white border-b border-slate-200 sticky top-0 z-[5]">
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Date / Time</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Qty</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Balance</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Job Card</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Stage</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden lg:table-cell">Worker</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase hidden sm:table-cell">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.transactions.map((txn, idx) => {
                      const isAdd = ['RECEIPT', 'INWARD', 'RETURN'].includes(txn.transaction_type);
                      return (
                        <tr key={txn.id || idx} className="border-b border-slate-200 hover:bg-white/50 transition-colors">
                          <td className="px-4 py-3"><TxnTypeBadge type={txn.transaction_type} /></td>
                          <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                            {txn.created_at ? new Date(txn.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                          <td className={`px-4 py-3 text-right font-mono font-bold ${isAdd ? 'text-emerald-600' : 'text-red-600'}`}>
                            {isAdd ? '+' : '-'}{parseFloat(txn.quantity || 0).toFixed(3)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">
                            {parseFloat(txn.balance_after || 0).toFixed(3)}
                          </td>
                          <td className="px-4 py-3 text-xs text-blue-600 font-mono hidden md:table-cell">{txn.job_card_code || '—'}</td>
                          <td className="px-4 py-3 text-xs text-slate-600 hidden md:table-cell">{txn.stage_code || txn.stage_name || '—'}</td>
                          <td className="px-4 py-3 text-xs text-slate-500 hidden lg:table-cell">{txn.worker_name || '—'}</td>
                          <td className="px-4 py-3 text-xs text-slate-500 hidden sm:table-cell truncate max-w-[180px]" title={txn.context_note || txn.reference_note || ''}>
                            {txn.context_note || txn.reference_note || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Transaction Pagination */}
            {ledger && ledger.transactions.length > 0 && (
              <div className="px-4 sm:px-6 py-3 border-t border-slate-200 flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  {ledger.transactions.length} transaction{ledger.transactions.length !== 1 ? 's' : ''}
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setTxnPage(p => Math.max(1, p - 1))} disabled={txnPage <= 1}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-30 flex items-center gap-1">
                    <ChevronLeft size={14} /> Prev
                  </button>
                  <button onClick={() => setTxnPage(p => p + 1)} disabled={ledger.transactions.length < 30}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-30 flex items-center gap-1">
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ─── MAIN: MATERIAL MASTER SCREEN ────────────────────────────
const MaterialMaster = ({ canEdit = false }) => {
  const { getValues } = useMasterData('MATERIAL_TYPE');
  const materialTypes = getValues('MATERIAL_TYPE');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  // Modal/detail states
  const [showModal, setShowModal] = useState(false);
  const [editMaterial, setEditMaterial] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null); // detail view
  const [confirmDialog, setConfirmDialog] = useState({ open: false, material: null });
  const [toast, setToast] = useState({ message: '', type: '' });

  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE };
      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      if (statusFilter === 'active') params.is_active = 'true';
      else if (statusFilter === 'inactive') params.is_active = 'false';
      const res = await authApi.get('/inventory/materials', { params });
      setMaterials(res.data.materials || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch { setToast({ message: 'Failed to load materials', type: 'error' }); }
    finally { setLoading(false); }
  }, [page, search, typeFilter, statusFilter]);

  useEffect(() => { fetchMaterials(); }, [fetchMaterials]);

  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSave = async (formData) => {
    if (!canEdit) {
      setToast({ message: 'You do not have permission to modify materials', type: 'error' });
      return;
    }
    if (editMaterial) {
      await authApi.put(`/inventory/materials/${editMaterial.id}`, {
        name: formData.name, category: formData.category, color: formData.color,
        purity: formData.purity, uom: formData.unit,
        price: parseFloat(formData.price) || 0,
        reorder_level: parseFloat(formData.reorder_level) || 0,
        reorder_quantity: parseFloat(formData.reorder_quantity) || 0,
        stone_quality_grade: formData.stone_quality_grade || null,
        stone_clarity: formData.stone_clarity || null,
        stone_cut: formData.stone_cut || null,
        stone_color: formData.stone_color || null,
        stone_type: formData.stone_type || null,
        custom_stone_type: formData.custom_stone_type || null,
        carat_weight: formData.carat_weight === '' ? null : parseFloat(formData.carat_weight),
        metadata_json: { shape: formData.shape || null, dimensions: formData.dimensions || null,
          hazard_type: formData.hazard_type || null, description: formData.description || null,
          stone_type: formData.stone_type || null, custom_stone_type: formData.custom_stone_type || null,
          carat_weight: formData.carat_weight === '' ? null : parseFloat(formData.carat_weight) }
      });
      setToast({ message: 'Material updated', type: 'success' });
    } else {
      await authApi.post('/inventory/materials', {
        name: formData.name, code: formData.code || undefined, type: formData.type,
        material_category: formData.type, category: formData.category,
        color: formData.color, purity: formData.purity, unit: formData.unit,
        price: parseFloat(formData.price) || 0,
        reorder_level: parseFloat(formData.reorder_level) || 0,
        reorder_quantity: parseFloat(formData.reorder_quantity) || 0,
        stone_quality_grade: formData.stone_quality_grade || null,
        stone_clarity: formData.stone_clarity || null,
        stone_cut: formData.stone_cut || null,
        stone_color: formData.stone_color || null,
        stone_type: formData.stone_type || null,
        custom_stone_type: formData.custom_stone_type || null,
        carat_weight: formData.carat_weight === '' ? null : parseFloat(formData.carat_weight),
        shape: formData.shape || null, dimensions: formData.dimensions || null,
        hazard_type: formData.hazard_type || null, description: formData.description || null
      });
      setToast({ message: 'Material created', type: 'success' });
    }
    setEditMaterial(null);
    fetchMaterials();
  };

  const handleDeactivate = async (mat) => {
    if (!canEdit) {
      setToast({ message: 'You do not have permission to modify materials', type: 'error' });
      return;
    }
    try {
      await authApi.delete(`/inventory/materials/${mat.id}`);
      setToast({ message: `${mat.name} deactivated`, type: 'success' });
      fetchMaterials();
    } catch (err) {
      setToast({ message: err?.response?.data?.error || 'Failed to deactivate', type: 'error' });
    }
    setConfirmDialog({ open: false, material: null });
  };

  const openEdit = (e, mat) => {
    e.stopPropagation();
    if (!canEdit) return;
    setEditMaterial(mat);
    setShowModal(true);
  };
  const openCreate = () => {
    if (!canEdit) return;
    setEditMaterial(null);
    setShowModal(true);
  };

  const typeColor = (t) => {
    const map = { METAL: 'text-amber-600', STONE: 'text-purple-600', CONSUMABLE: 'text-orange-600', FINDING: 'text-cyan-600' };
    return map[t] || 'text-slate-500';
  };

  // ── If a material is selected, show detail view ──
  if (selectedMaterial) {
    return (
      <>
        {toast.message && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />}
        <MaterialDetail
          material={selectedMaterial}
          onBack={() => { setSelectedMaterial(null); fetchMaterials(); }}
          onToast={(msg, type) => setToast({ message: msg, type })}
          canEdit={canEdit}
        />
      </>
    );
  }

  // ── Material list view ──
  return (
    <div className="h-full flex flex-col bg-slate-50 text-slate-800">
      {toast.message && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />}
      <ConfirmDialog isOpen={confirmDialog.open} title="Deactivate Material"
        message={`Are you sure you want to deactivate "${confirmDialog.material?.name}"?`}
        onConfirm={() => handleDeactivate(confirmDialog.material)}
        onCancel={() => setConfirmDialog({ open: false, material: null })}
        confirmLabel="Deactivate" danger />
      <MaterialFormModal isOpen={showModal}
        onClose={() => { setShowModal(false); setEditMaterial(null); }}
        onSave={handleSave} material={editMaterial} />

      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Package size={22} className="text-blue-600" /> Material Master
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">{total} material{total !== 1 ? 's' : ''} total — click a row to view batch history</p>
        </div>
        {canEdit && (
          <button onClick={openCreate}
            className="min-h-[44px] flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg shadow-lg shadow-blue-600/20 transition-colors">
            <Plus size={16} /> Add Material
          </button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="px-4 sm:px-6 py-3 border-b border-slate-200 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or code..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:border-blue-500 outline-none transition-colors" />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-500 hidden sm:block" />
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:border-blue-500 outline-none">
            <option value="">All Types</option>
            {materialTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {[{ value: 'active', label: 'Active' }, { value: 'all', label: 'All' }, { value: 'inactive', label: 'Inactive' }].map(opt => (
            <button key={opt.value} onClick={() => { setStatusFilter(opt.value); setPage(1); }}
              className={`px-3 py-2 text-xs font-bold transition-colors ${statusFilter === opt.value ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <Loader2 className="animate-spin mb-2 text-blue-500" size={28} /><p className="text-sm">Loading materials...</p>
          </div>
        ) : materials.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <Package size={40} className="mb-2 opacity-30" /><p className="text-sm">No materials found</p>
            {canEdit && <button onClick={openCreate} className="mt-3 text-blue-600 text-sm hover:underline">+ Add your first material</button>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white border-b border-slate-200 sticky top-0 z-[5]">
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Code</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider hidden md:table-cell">UOM</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Stock</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Reorder</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Cost Price</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  {canEdit && <th className="text-center px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {materials.map((m) => {
                  const isLow = m.stock < m.reorder_level && m.reorder_level > 0;
                  const hasActiveBatches = m.stock > 0;
                  return (
                    <tr key={m.id} onClick={() => setSelectedMaterial(m)}
                      className="border-b border-slate-200 hover:bg-white/50 transition-colors cursor-pointer">
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{m.code || '—'}</td>
                      <td className="px-4 py-3 font-medium text-slate-800">{m.name}</td>
                      <td className={`px-4 py-3 text-xs font-bold hidden sm:table-cell ${typeColor(m.type)}`}>{m.type}</td>
                      <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{m.category || '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs hidden md:table-cell">{m.unit}</td>
                      <td className={`px-4 py-3 text-right font-mono font-bold ${isLow ? 'text-red-600' : 'text-emerald-600'}`}>
                        <span className="flex items-center justify-end gap-1">
                          {isLow && <AlertTriangle size={12} className="text-red-600" />}
                          {m.stock.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-500 hidden sm:table-cell">{m.reorder_level.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-500 hidden lg:table-cell">{m.price.toFixed(2)}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={m.status} isActive={m.is_active} /></td>
                      {canEdit && (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={(e) => openEdit(e, m)} title="Edit"
                              className="min-h-[44px] p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-blue-600">
                              <Edit2 size={14} />
                            </button>
                            {m.is_active !== false && (
                              <button onClick={(e) => { e.stopPropagation(); setConfirmDialog({ open: true, material: m }); }}
                                title={hasActiveBatches ? 'Has active stock' : 'Deactivate'}
                                disabled={hasActiveBatches}
                                className={`min-h-[44px] p-1.5 rounded-lg transition-colors ${hasActiveBatches
                                  ? 'text-gray-600 cursor-not-allowed' : 'hover:bg-slate-100 text-slate-500 hover:text-red-600'}`}>
                                <Ban size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 sm:px-6 py-3 border-t border-slate-200 flex items-center justify-between">
          <p className="text-xs text-slate-500">Page {page} of {totalPages} ({total} items)</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-30 flex items-center gap-1">
              <ChevronLeft size={14} /> Prev
            </button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-30 flex items-center gap-1">
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialMaster;
